import { DayOfWeek } from '../../data/entities/booking/DayOfWeek';
import ScheduleBlockEditor from './ScheduleBlockEditor';
import { Row, Col } from 'react-bootstrap';
import { Icon } from '../icons';
import style from './styles/Schedule.module.scss';
import { FieldArray, useFormikContext } from 'formik';
import { minutesToTimeString, timeStringToMinutes } from '../../utils';

const daysOfWeek = [
  { name: 'SUN', value: DayOfWeek.Sunday },
  { name: 'MON', value: DayOfWeek.Monday },
  { name: 'TUE', value: DayOfWeek.Tuesday },
  { name: 'WED', value: DayOfWeek.Wednesday },
  { name: 'THU', value: DayOfWeek.Thursday },
  { name: 'FRI', value: DayOfWeek.Friday },
  { name: 'SAT', value: DayOfWeek.Saturday },
];

const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '17:00';
const NEW_BLOCK_DURATION_MINUTES = 60;
const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

type BlockTimes = { startTime: string; endTime: string };

function blockStartingAt(start: number, limit: number): BlockTimes {
  return {
    startTime: minutesToTimeString(start),
    endTime: minutesToTimeString(Math.min(start + NEW_BLOCK_DURATION_MINUTES, limit)),
  };
}
function nextBlockTimes(blocksForDay: Array<{ startTime?: string; endTime?: string }>): BlockTimes | null {
  const blocks = blocksForDay
    .map((block) => ({ start: timeStringToMinutes(block.startTime), end: timeStringToMinutes(block.endTime) }))
    .filter(({ start, end }) => !Number.isNaN(start) && !Number.isNaN(end))
    .sort((a, b) => a.start - b.start);

  if (blocks.length === 0)
    return { startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME };

  const latestEnd = Math.max(...blocks.map((block) => block.end));
  if (latestEnd < LAST_MINUTE_OF_DAY)
    return blockStartingAt(latestEnd, LAST_MINUTE_OF_DAY);

  let coveredUntil = blocks[0].end;
  for (const block of blocks.slice(1)) {
    if (block.start > coveredUntil)
      return blockStartingAt(coveredUntil, block.start);
    coveredUntil = Math.max(coveredUntil, block.end);
  }

  return null;
}

function getDayError(errors: unknown, day: DayOfWeek): string | undefined {
  const blockErrors = (errors as { scheduleBlocks?: unknown } | undefined)?.scheduleBlocks;
  if (!blockErrors || typeof blockErrors !== 'object') return undefined;

  const dayError = (blockErrors as Record<string, unknown>)[String(day)];
  return typeof dayError === 'string' ? dayError : undefined;
}

export const ScheduleEditor = () => {

  const { values, errors } = useFormikContext<any>();

  return (
    <FieldArray name="scheduleBlocks">
      {({ push, remove }) => (
        <>
          {daysOfWeek.map((day) => {
            const blocksForDay = values.scheduleBlocks.filter(
              (block: any) => block.dayOfWeek === day.value
            );
            const dayError = getDayError(errors, day.value);
            const nextBlock = nextBlockTimes(blocksForDay);

            return (
              <Row key={day.value} className="align-items-start">
                <Col xs={2} className="fw-bold me-3 py-2">
                  {day.name}
                </Col>
                <Col className="d-flex align-items-start justify-content-between">
                  <div>
                    {blocksForDay.map((block: any) => {
                      const blockIndex = values.scheduleBlocks.findIndex(
                        (b: any) => b === block
                      );
                      return (
                        <ScheduleBlockEditor
                          key={block.id || blockIndex}
                          blockIndex={blockIndex}
                          remove={remove}
                        />
                      );
                    })}
                    {dayError && (
                      <div className="text-danger small pb-2">{dayError}</div>
                    )}
                  </div>
                  <div className="py-2" title={nextBlock ? 'Add time' : 'No time left in this day'}>
                    <Icon
                      iconName="plus"
                      className={`${style.add} w-auto ${nextBlock ? 'cursor-pointer' : style.addDisabled}`}
                      onClick={nextBlock
                        ? () =>
                          push({
                            scheduleId: values.scheduleId,
                            dayOfWeek: day.value,
                            ...nextBlock,
                          })
                        : undefined}
                    />
                  </div>
                </Col>
                <hr className="my-2"/>
              </Row>
            );
          })}
        </>
      )}
    </FieldArray>
  );
};

export default ScheduleEditor;
